import type { LucideIcon } from 'lucide-react';
import { AlertCircle } from 'lucide-react';
import { Button } from '@ai-tutor/ui';

/**
 * Reusable error-state card.
 *
 * Three visual sizes - `page` for full-screen fallbacks (root
 * ErrorBoundary, lesson 404, 500), `panel` for inside a single content
 * surface (`ArtifactGate` failed branch, sidebar panel boundary), and
 * `inline` for compact "this section failed" callouts within a larger
 * layout.
 *
 * Replaces the hand-rolled cards in `LessonLearningPage` (overview
 * failure), `MediaViewer.tsx` (`ArtifactGate`'s failed branch),
 * and `ErrorBoundary.tsx`'s default fallback so the look stays
 * consistent across the app.
 */

interface ErrorStateAction {
  label: string;
  onClick: () => void;
  loading?: boolean;
}

export interface ErrorStateProps {
  variant?: 'page' | 'panel' | 'inline';
  icon?: LucideIcon;
  title: string;
  description?: string;
  primaryAction?: ErrorStateAction;
  secondaryAction?: ErrorStateAction;
  className?: string;
}

export function ErrorState({
  variant = 'panel',
  icon: Icon = AlertCircle,
  title,
  description,
  primaryAction,
  secondaryAction,
  className = '',
}: ErrorStateProps) {
  if (variant === 'inline') {
    return (
      <div
        className={`flex items-center gap-3 rounded-md border border-destructive/20 bg-destructive/5 px-3 py-2 text-sm ${className}`}
        role='alert'
      >
        <Icon className='h-4 w-4 flex-shrink-0 text-destructive-emphasis' />
        <div className='flex-1'>
          <span className='font-medium text-foreground'>{title}</span>
          {description && (
            <span className='ml-2 text-muted-foreground'>{description}</span>
          )}
        </div>
        {primaryAction && (
          <Button
            variant='ghost'
            size='sm'
            onClick={primaryAction.onClick}
            disabled={primaryAction.loading}
            // Drawn at the strip's 32px, tapped as 44 on a phone.
            className='tap-target'
          >
            {primaryAction.label}
          </Button>
        )}
      </div>
    );
  }

  const sizing =
    variant === 'page'
      ? {
          // No background fill: nested usages (inside AppLayout) show
          // the underlying GridBackground, and the root ErrorBoundary
          // case is rare enough that a transparent fallback over the
          // browser default is acceptable.
          wrapper: 'flex min-h-full items-center justify-center p-4',
          inner: 'max-w-md text-center',
          iconBox:
            'mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-red-100 dark:bg-destructive/20',
          iconClass: 'h-8 w-8 text-red-600 dark:text-destructive-emphasis',
          titleClass: 'mb-2 text-2xl font-bold text-foreground',
          descClass: 'mb-6 text-muted-foreground',
        }
      : {
          wrapper: 'flex h-full flex-col items-center justify-center p-6',
          inner: 'max-w-sm text-center',
          iconBox:
            'mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-destructive/20',
          iconClass: 'h-6 w-6 text-red-600 dark:text-destructive-emphasis',
          titleClass: 'mb-2 text-lg font-semibold text-foreground',
          descClass: 'mb-4 text-sm text-muted-foreground',
        };

  return (
    <div className={`${sizing.wrapper} ${className}`} role='alert'>
      <div className={sizing.inner}>
        <div className={sizing.iconBox}>
          <Icon className={sizing.iconClass} />
        </div>
        <h2 className={sizing.titleClass}>{title}</h2>
        {description && <p className={sizing.descClass}>{description}</p>}
        {(primaryAction || secondaryAction) && (
          // 44px buttons on a phone: the only way out of a failed panel
          // should be the easiest thing on the screen to hit.
          <div className='flex flex-wrap items-center justify-center gap-3 max-sm:[&>button]:h-11'>
            {primaryAction && (
              <Button
                onClick={primaryAction.onClick}
                disabled={primaryAction.loading}
              >
                {primaryAction.label}
              </Button>
            )}
            {secondaryAction && (
              <Button
                variant='outline'
                onClick={secondaryAction.onClick}
                disabled={secondaryAction.loading}
              >
                {secondaryAction.label}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
